import { useState } from 'react';

export default function HomePage() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d6fc0fn1jbcgr9kd6s30', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setFormStatus('Thank you for subscribing!');
        setEmail('');
        setTimeout(() => setFormStatus(''), 3000);
      } else {
        setFormStatus('Something went wrong. Please try again.');
      }
    } catch (error) {
      setFormStatus('Error submitting form. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* Hero Section */}
      <section id="home" className="flex min-h-screen">
        {/* Left Content */}
        <div className="w-[55%] bg-white flex flex-col justify-center pl-20 pr-12">
          <div className="mb-8">
            <img 
              src="https://public.readdy.ai/ai/img_res/99dc7b78-bdba-402c-9bf2-1a7da4f3fa7e.png" 
              alt="BLIST D.O.O." 
              className="h-16 w-auto object-contain"
            />
          </div>
          
          <h1 className="text-[78px] font-[200] leading-[1.1] tracking-[0.02em] text-black mb-8">
            Modern iOS<br />
            Applications For<br />
            Enhanced Security
          </h1>
          
          <p className="text-lg text-gray-600 leading-relaxed max-w-xl">
            BLIST D.O.O. is a team of experts developing cutting-edge applications for the iOS platform. 
            Our products, including VPN and Cleaner solutions, are designed to enhance user experience 
            and improve the security of mobile devices.
          </p>
        </div>

        {/* Right Visual */}
        <div className="w-[45%] bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6] relative overflow-hidden flex items-center justify-center">
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>
          
          <div className="relative z-10 transform rotate-[15deg]" style={{ filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.3))' }}>
            <img 
              src="https://public.readdy.ai/ai/img_res/e4c27d3b8e1b3c24d1d19cb3ae4c6545.jpg"
              alt="iOS App Interface"
              className="w-[320px] h-auto object-contain"
            />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 flex">
        {/* Left Decorative */}
        <div className="w-[40%] bg-gradient-to-br from-[#F3F4F6] to-[#E0F2FE] relative overflow-hidden">
          <div className="absolute top-20 left-12 w-64 h-64 bg-pink-100/60 rounded-[60px] transform rotate-12"></div>
          <div className="absolute top-40 right-16 w-48 h-48 bg-blue-100/70 rounded-[50px] transform -rotate-6"></div>
          <div className="absolute bottom-32 left-24 w-56 h-56 bg-purple-100/50 rounded-[55px] transform rotate-6"></div>
        </div>

        {/* Right Content */}
        <div className="w-[60%] bg-white flex items-center pl-20 pr-20">
          <div className="w-full">
            <h2 className="text-[56px] font-bold leading-[1.2] text-black mb-20 max-w-2xl">
              Expert Team Behind<br />Your Mobile Security
            </h2>
            
            <div className="flex justify-center">
              <div className="text-center">
                <div className="text-[68px] font-extrabold text-cyan-500 mb-2">24/7</div>
                <div className="text-base text-gray-600">Support Available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 px-20 bg-white">
        <div className="mb-24">
          <h2 className="text-[60px] font-[200] text-black mb-4 max-w-xl">
            Our Premium iOS Solutions
          </h2>
          <p className="text-lg text-gray-600">Designed for performance, built for security</p>
        </div>

        <div className="grid grid-cols-3 gap-10">
          {/* VPN Card */}
          <div className="rounded-[32px] bg-gradient-to-b from-[#E0F2FE] to-white p-8 pb-10">
            <div className="mb-6 flex items-center justify-center h-[280px]">
              <img 
                src="https://readdy.ai/api/search-image?query=Premium%20VPN%20application%20interface%20on%20iPhone%20screen%20showing%20secure%20connection%20with%20blue%20shield%20icon%20and%20world%20map%2C%20clean%20modern%20UI%20design%2C%20minimalist%20style%2C%20light%20blue%20gradient%20background%2C%20professional%20app%20screenshot%2C%20high%20quality%20digital%20product%20image&width=240&height=480&seq=vpn-product-001&orientation=portrait"
                alt="SecureVPN Pro"
                className="h-full w-auto object-contain"
              />
            </div>
            
            <h3 className="text-2xl font-semibold text-center mb-2">SecureVPN Pro</h3>
            <p className="text-xl text-center text-gray-700 mb-6">Premium Protection</p>
            
            <div className="flex justify-center mb-4">
              <div className="bg-black text-white px-8 py-3 rounded-full text-sm font-medium whitespace-nowrap">
                Enterprise Grade
              </div>
            </div>
            
            <p className="text-xs text-gray-500 text-center">Military-grade encryption</p>
          </div>

          {/* Cleaner Card */}
          <div className="rounded-[32px] bg-white border border-gray-200 p-8 pb-10">
            <div className="mb-6 flex items-center justify-center h-[280px] bg-gray-50 rounded-2xl">
              <img 
                src="https://readdy.ai/api/search-image?query=iOS%20cleaner%20application%20interface%20displaying%20storage%20optimization%20with%20circular%20progress%20chart%20and%20clean%20icons%2C%20modern%20minimalist%20design%2C%20white%20and%20gray%20color%20scheme%2C%20professional%20app%20mockup%2C%20simple%20background%2C%20high%20quality%20product%20screenshot&width=240&height=480&seq=cleaner-product-001&orientation=portrait"
                alt="SmartCleaner Plus"
                className="h-full w-auto object-contain"
              />
            </div>
            
            <h3 className="text-2xl font-semibold text-center mb-2">SmartCleaner Plus</h3>
            <p className="text-xl text-center text-gray-700 mb-6">Optimize Performance</p>
            
            <div className="flex justify-center mb-4">
              <div className="bg-white border-2 border-black text-black px-8 py-3 rounded-full text-sm font-medium whitespace-nowrap">
                Advanced Cleaning
              </div>
            </div>
            
            <p className="text-xs text-gray-500 text-center">Free up to 70% storage</p>
          </div>

          {/* Suite Card */}
          <div className="rounded-[32px] bg-white border border-gray-200 p-8 pb-10">
            <div className="mb-6 flex items-center justify-center h-[280px] bg-gray-50 rounded-2xl">
              <img 
                src="https://readdy.ai/api/search-image?query=Combined%20iOS%20app%20suite%20interface%20showing%20VPN%20and%20cleaner%20features%20together%2C%20dual%20functionality%20display%20with%20security%20shield%20and%20optimization%20icons%2C%20modern%20clean%20design%2C%20light%20gray%20background%2C%20professional%20product%20mockup%2C%20minimalist%20aesthetic%2C%20high%20quality%20digital%20image&width=240&height=480&seq=suite-product-001&orientation=portrait"
                alt="Complete Suite"
                className="h-full w-auto object-contain"
              />
            </div>
            
            <h3 className="text-2xl font-semibold text-center mb-2">Complete Suite</h3>
            <p className="text-xl text-center text-gray-700 mb-6">All-in-One Solution</p>
            
            <div className="flex justify-center mb-4">
              <div className="bg-white border-2 border-black text-black px-8 py-3 rounded-full text-sm font-medium whitespace-nowrap">
                Full Protection
              </div>
            </div>
            
            <p className="text-xs text-gray-500 text-center">VPN + Cleaner bundle</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 flex bg-white">
        {/* Left Content */}
        <div className="w-[58%] flex items-center pl-20 pr-16">
          <div>
            <div className="flex items-center gap-3 mb-20">
              <div className="w-2 h-2 bg-black rounded-full"></div>
              <span className="text-[13px] uppercase tracking-[0.04em] text-gray-600 font-medium">CLIENT FEEDBACK</span>
            </div>
            
            <div className="mb-10">
              <span className="text-[120px] leading-none text-gray-300 font-serif">"</span>
            </div>
            
            <blockquote className="text-[46px] leading-[1.4] font-serif text-gray-900 mb-10">
              BLIST D.O.O. transformed our mobile security approach with their exceptional VPN solution
            </blockquote>
            
            <div className="flex items-center gap-3">
              <div>
                <div className="text-lg font-bold text-black">Michael Richardson</div>
                <div className="flex items-center gap-2 mt-1">
                  <i className="ri-checkbox-circle-fill text-green-500 text-base"></i>
                  <span className="text-sm text-gray-600">Verified User</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Visual */}
        <div className="w-[42%] flex items-center justify-center py-16 pr-20">
          <img 
            src="https://readdy.ai/api/search-image?query=Professional%20business%20portrait%20of%20confident%20male%20executive%20in%20modern%20office%2C%20dramatic%20side%20lighting%2C%20black%20and%20white%20photography%2C%20head%20tilted%20slightly%20upward%2C%20shoulders%20up%20framing%2C%20clean%20white%20background%2C%20high%20contrast%20studio%20lighting%2C%20corporate%20headshot%20style%2C%20professional%20quality&width=400&height=600&seq=testimonial-portrait-001&orientation=portrait"
            alt="Client Testimonial"
            className="w-full h-[500px] object-cover rounded-3xl"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-36 px-20 bg-white">
        <div className="max-w-[85%] mx-auto">
          <div className="rounded-[28px] overflow-hidden h-[480px] relative mb-16">
            <img 
              src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20with%20MacBook%20displaying%20iOS%20app%20development%20interface%2C%20clean%20desk%20setup%2C%20soft%20natural%20lighting%2C%20professional%20office%20environment%2C%20technology%20and%20productivity%20theme%2C%20muted%20color%20palette%2C%20film%20grain%20texture%2C%20high%20quality%20photography%2C%20wide%20angle%20view&width=1200&height=480&seq=cta-workspace-001&orientation=landscape"
              alt="Workspace"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/15"></div>
          </div>
          
          <div className="text-center">
            <h2 className="text-[42px] leading-[1.5] tracking-[0.01em] mb-12">
              <span className="font-semibold text-black">Elevate your iOS experience</span><br />
              <span className="font-light italic text-gray-500">with cutting-edge security</span><br />
              <span className="font-light italic text-gray-500">and performance optimization</span>
            </h2>
            
            <div className="flex justify-center">
              <a 
                href="#products" 
                className="bg-black text-white px-12 py-4 rounded-full text-base font-medium whitespace-nowrap inline-block cursor-pointer hover:bg-gray-900 transition-colors"
              >
                Explore Solutions
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <footer id="contact" className="m-5 rounded-3xl bg-[#1F2937] relative overflow-hidden">
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-[190px] font-bold text-[#2D3748] opacity-40 leading-none select-none pointer-events-none">
          BLIST
        </div>
        
        <div className="relative z-10 px-20 py-20">
          <div className="grid grid-cols-12 gap-16">
            {/* Brand */}
            <div className="col-span-4">
              <h3 className="text-[56px] font-bold text-white leading-tight mb-20">
                BLIST D.O.O.
              </h3>
            </div>

            {/* Address */}
            <div className="col-span-2">
              <h4 className="text-base font-semibold text-white uppercase tracking-[0.03em] mb-6">
                LOCATION
              </h4>
              <address className="not-italic text-sm text-gray-300 leading-[1.8]">
                KRALjA ALEKSANDRA<br />
                OBRENOVICA 13<br />
                City Belosevac<br />
                Zip 258432<br />
                Country Serbia
              </address>
            </div>

            {/* Contact */}
            <div className="col-span-3">
              <h4 className="text-base font-semibold text-white uppercase tracking-[0.03em] mb-6">
                CONTACT
              </h4>
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-mail-line text-lg text-gray-300"></i>
                  </div>
                  <a href="mailto:help@blistdoo.com" className="text-sm text-gray-300 hover:text-white transition-colors">
                    help@blistdoo.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line text-lg text-gray-300"></i>
                  </div>
                  <a href="tel:+19567523556" className="text-sm text-gray-300 hover:text-white transition-colors">
                    +1 9567523556
                  </a>
                </div>
              </div>
            </div>

            {/* Newsletter */}
            <div className="col-span-3">
              <p className="text-[15px] text-white leading-relaxed mb-8">
                Stay updated with our<br />latest iOS innovations
              </p>
              
              <form onSubmit={handleNewsletterSubmit} data-readdy-form id="newsletter-form" className="relative">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  required
                  className="w-full h-[52px] bg-[#374151] border border-[#4B5563] rounded-full pl-6 pr-16 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-cyan-500 transition-colors"
                />
                <button
                  type="submit"
                  className="absolute right-[-2px] top-1/2 transform -translate-y-1/2 w-14 h-14 bg-white rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors"
                >
                  <i className="ri-arrow-right-line text-xl text-black"></i>
                </button>
              </form>
              
              {formStatus && (
                <p className="mt-3 text-sm text-cyan-400">{formStatus}</p>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
